import React from 'react';
import { api, ServerAuditReport } from '../api';
import { Section } from './primitives';
import { OperationFailure, OperationResult, useOperationRun } from './operation/OperationResult';
import { actionLabel } from './operation/actionLabels';
import { SshStat, SshStats } from './SshStats';
import { StatusBadge } from './ui';

/** The audit reports the overall verdict as compatible/incompatible; a compatible stack passed. */
interface ServerAuditCardProps {
  environmentId: string;
}

const verdictOf: Record<ServerAuditReport['overall_status'], { tone: 'success' | 'warning' | 'danger' | 'neutral'; verdict: string }> = {
  compatible: { tone: 'success', verdict: 'The server is compatible' },
  warning: { tone: 'warning', verdict: 'The server is compatible, with warnings' },
  incompatible: { tone: 'danger', verdict: 'The server is not compatible' },
};

/** What the audit ran, counted from the checks the server returned. */
function coverage(report: ServerAuditReport): string {
  const total = report.checks.length;
  if (total === 0) return 'The server returned no individual checks.';
  const count = (status: string) => report.checks.filter((c) => c.status === status).length;
  return `${total} ${total === 1 ? 'check' : 'checks'}: ${count('passed')} passed, ${count('warning')} with a warning, ${count('failed')} failed.`;
}

export const ServerAuditCard: React.FC<ServerAuditCardProps> = ({ environmentId }) => {
  const { running, result, failure, run } = useOperationRun(() => api.getServerAudit(environmentId), 'The server gave no reason.');
  const report = result?.value ?? null;
  const incomplete = report && !report.succeeded ? report : null;
  const answer =
    report && report.succeeded
      ? (verdictOf[report.overall_status] ?? { tone: 'neutral' as const, verdict: `The audit finished with a verdict this page does not know (${report.overall_status})` })
      : null;

  return (
    <Section
      title="Server compatibility"
      testId="server-audit"
      actions={
        <button type="button" onClick={run} disabled={running} className="button button--secondary">
          {running ? 'Auditing the server' : failure ? actionLabel.retry : report ? 'Run the audit again' : 'Run server audit'}
        </button>
      }
    >
      {failure ? (
        <OperationFailure
          verdict="The server audit did not finish"
          at={failure.at}
          scope="Nothing was changed"
          reason={failure.message}
          testId="server-audit-failure"
        />
      ) : null}

      {incomplete && result ? (
        <OperationFailure
          verdict="The server audit did not finish"
          at={result.at}
          scope="Incomplete: score and checks are not shown"
          output={incomplete.error_output}
          testId="server-audit-incomplete"
        />
      ) : null}

      {report && answer && result ? (
        <OperationResult tone={answer.tone} verdict={answer.verdict} at={result.at} scope={coverage(report)} testId="server-audit-result">
          <SshStats>
            {/* The verdict above already says compatible, warning or not: the score is the extra fact. */}
            <SshStat label="Compatibility score" value={`${report.compatibility_score}/100`} />
            <SshStat label="PHP" value={report.php_version || 'Not found'} />
            <SshStat
              label="Redis"
              value={report.redis_detected ? 'Running' : 'Not found'}
            />
            <SshStat
              label="Search service"
              value={report.opensearch_detected ? 'Answers on 9200' : 'None'}
              detail={report.opensearch_detected ? undefined : 'Only needed if the code uses one'}
            />
            <SshStat label="Free disk" value={`${(report.disk_free_bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`} />
          </SshStats>

          <ul className="status-list status-list--stacked" aria-label="Server checks">
            {report.checks.map((c, i) => (
              <li key={`${c.name}-${i}`} className="status-list__item">
                <span className="status-list__name">
                  <span>{c.name}</span> <span className="ssh-meta">{c.category}</span>
                </span>
                <StatusBadge kind="auditResult" value={c.status} />
                <div className="status-list__detail">
                  {c.message}
                  {c.observed_value ? (
                    <div className="muted">
                      Found <code>{c.observed_value}</code>
                    </div>
                  ) : null}
                  {c.recommendation ? <div className="ssh-advice">{c.recommendation}</div> : null}
                </div>
              </li>
            ))}
          </ul>
        </OperationResult>
      ) : null}

      {!report && !failure ? <p className="quiet-empty">Not audited yet</p> : null}
    </Section>
  );
};
